import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// ==================== DISHES ====================
export const getDishes = async (restaurantId, locationId) => {
  // With a location in scope each dish carries `available_here`; page_size=1000, since callers expect the FULL list.
  const qs = `?page_size=1000${locationId ? `&location_id=${locationId}` : ''}`;
  const url = `${API_URL}/users/restaurant/${restaurantId}/dishes/${qs}`;
  console.log('menuService.getDishes: Calling', url);
  
  try {
    const response = await axios.get(url);
    console.log('menuService.getDishes: Raw Response:', response);
    console.log('menuService.getDishes: Response Data:', response.data);
    
    if (response.data.success) {
      // The backend wraps the payload as { items, page, ... }, so unwrap items for callers expecting an array.
      const payload = response.data.data;
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.items)
          ? payload.items
          : [];
      console.log('menuService.getDishes: Success! Dishes:', list.length);
      return list;
    } else {
      console.error('menuService.getDishes: API returned success=false', response.data);
      throw new Error(response.data.error || response.data.message || 'Failed to load dishes');
    }
  } catch (error) {
    console.error('menuService.getDishes: Error Details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      url: url
    });
    throw error.response?.data || { error: error.message };
  }
};

// ONE call for the menu builder: every dish of every type with variants and category, filtered client-side.
export const getAllDishes = async (restaurantId, locationId) => {
  try {
    const qs = locationId ? `?location_id=${locationId}` : '';
    const response = await axios.get(
      `${API_URL}/users/restaurant/${restaurantId}/getAllDishes/${qs}`,
    );
    return response.data?.success ? (response.data.data || []) : [];
  } catch (error) {
    console.error('menuService.getAllDishes: Error', error);
    return [];
  }
};

export const getActiveDishes = async (restaurantId) => {
  try {
    const response = await axios.get(`${API_URL}/users/restaurant/${restaurantId}/dishes/active/`);
    return response.data.success ? response.data.data : [];
  } catch (error) {
    console.error('menuService.getActiveDishes: Error', error);
    return [];
  }
};

export const getAllBeverages = async (restaurantId) => {
  try {
    // Get all dishes and filter beverages on frontend
    const response = await axios.get(`${API_URL}/users/restaurant/${restaurantId}/dishes/`);
    const payload = response.data.success ? response.data.data : [];
    const allDishes = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.items)
        ? payload.items
        : [];
    return allDishes.filter(dish => dish.dish_type === 'beverage');
  } catch (error) {
    console.error('menuService.getAllBeverages: Error', error);
    return [];
  }
};

export const getDish = async (restaurantId, dishId) => {
  try {
    // There is no single-dish endpoint, so unwrap .items before searching, or the edit form loads stale data.
    const response = await axios.get(`${API_URL}/users/restaurant/${restaurantId}/dishes/?page_size=1000`);
    const payload = response.data.success ? response.data.data : [];
    const allDishes = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.items)
        ? payload.items
        : [];
    const dish = allDishes.find(d => d.id === parseInt(dishId));
    if (!dish) {
      throw new Error('Dish not found');
    }
    return dish;
  } catch (error) {
    console.error('menuService.getDish: Error', error);
    throw error.response?.data || error.message;
  }
};

export const createDish = async (restaurantId, dishData) => {
  try {
    let payload = dishData;
    let headers = {};

    if (dishData instanceof FormData) {
      dishData.append('restaurant_id', restaurantId);
      headers['Content-Type'] = 'multipart/form-data';
    } else {
      payload = { ...dishData, restaurant_id: restaurantId };
    }

    const response = await axios.post(`${API_URL}/users/dish/create/`, payload, { headers });
    return response.data;
  } catch (error) {
    console.error('menuService.createDish: Error', error);
    throw error.response?.data || error.message;
  }
};

export const updateDish = async (restaurantId, dishId, dishData) => {
  try {
    let headers = {};
    if (dishData instanceof FormData) {
      headers['Content-Type'] = 'multipart/form-data';
    }
    const response = await axios.put(`${API_URL}/users/restaurant/${restaurantId}/dish/${dishId}/update/`, dishData, { headers });
    return response.data;
  } catch (error) {
    console.error('menuService.updateDish: Error', error);
    throw error.response?.data || error.message;
  }
};

export const deleteDish = async (restaurantId, dishId) => {
  try {
    const response = await axios.delete(`${API_URL}/users/restaurant/${restaurantId}/dish/${dishId}/delete/`);
    return response.data;
  } catch (error) {
    console.error('menuService.deleteDish: Error', error);
    throw error.response?.data || error.message;
  }
};

// Flip a dish's Active/Inactive status as a partial update, distinct from the per-location 86 toggle.
export const setDishActive = async (restaurantId, dishId, isActive) => {
  try {
    const response = await axios.patch(
      `${API_URL}/users/restaurant/${restaurantId}/dish/${dishId}/update/`,
      { is_available: !!isActive },
    );
    return response.data;
  } catch (error) {
    console.error('menuService.setDishActive: Error', error);
    throw error.response?.data || error.message;
  }
};

// Turn a dish on or off for a single branch, leaving every other branch untouched.
export const setDishLocationAvailability = async (restaurantId, locationId, dishId, isAvailable) => {
  try {
    const response = await axios.patch(
      `${API_URL}/users/restaurant/${restaurantId}/locations/${locationId}/dishes/${dishId}/availability/`,
      { is_available: isAvailable },
    );
    return response.data;
  } catch (error) {
    console.error('menuService.setDishLocationAvailability: Error', error);
    throw error.response?.data || error.message;
  }
};

// Replace the FULL set of branches a dish is off at in one call, reactivating every other branch.
export const setDishLocationAvailabilityBulk = async (restaurantId, dishId, offLocationIds) => {
  try {
    const response = await axios.patch(
      `${API_URL}/users/restaurant/${restaurantId}/dishes/${dishId}/availability/`,
      { off_location_ids: offLocationIds },
    );
    return response.data;
  } catch (error) {
    console.error('menuService.setDishLocationAvailabilityBulk: Error', error);
    throw error.response?.data || error.message;
  }
};


// MENU PICKER: fed by getAllDishes, one fetch of every dish filtered client-side.

// MENUS: the one menu-list API returning every menu's metadata, filtered by callers in the UI.
export const getAllMenus = async (restaurantId) => {
  const url = `${API_URL}/users/restaurant/${restaurantId}/getAllMenus/`;
  try {
    const response = await axios.get(url);
    if (response.data?.success) {
      return response.data.data || [];
    }
    throw new Error(response.data?.error || response.data?.message || 'Failed to load menus');
  } catch (error) {
    if (error.response?.status === 404) return [];
    throw error.response?.data || { error: error.message };
  }
};

// Is a menu orderable now? Inactive never is, location-hours menus are left to the server, else compare the window.
export const isMenuAvailableNow = (menu, now = new Date()) => {
  if (!menu || menu.is_active === false) return false;
  if (menu.follow_location_hours) return true;
  const { active_from: from, active_until: until } = menu;
  if (!from && !until) return true;
  const toMin = (hhmm) => {
    const [h, m] = String(hhmm).split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
  };
  const cur = now.getHours() * 60 + now.getMinutes();
  const start = from ? toMin(from) : 0;
  const end = until ? toMin(until) : 24 * 60;
  // Normal window (start <= end) vs overnight window that wraps midnight.
  return start <= end ? cur >= start && cur <= end : cur >= start || cur <= end;
};

// Human label for a menu's serving window — shared by the menu screens.
export const menuTimingLabel = (menu) => {
  if (!menu) return '';
  if (menu.follow_location_hours) return 'Location hours';
  if (menu.active_from || menu.active_until) {
    return `${menu.active_from || '--:--'}–${menu.active_until || '--:--'}`;
  }
  return '';
};

// THE single menu-detail fetch: one location-scoped, order-ready menu shared by the View page and order screen.
export const getMenuById = async (restaurantId, locationId, menuId) => {
  try {
    const response = await axios.get(
      `${API_URL}/users/restaurant/${restaurantId}/getMenuById?location_id=${locationId}&menu_id=${menuId}`,
    );
    return response.data?.success ? (response.data.data || null) : null;
  } catch (error) {
    console.error('menuService.getMenuById: Error', error);
    return null;
  }
};

// Editor-only loader for the full unfiltered multi-location structure the builder needs.
export const getMenu = async (restaurantId, menuId) => {
  try {
    const response = await axios.get(`${API_URL}/users/restaurant/${restaurantId}/menus/${menuId}/`);
    return response.data.data || response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const createMenu = async (restaurantId, menuData) => {
  try {
    const response = await axios.post(`${API_URL}/users/restaurant/${restaurantId}/menus/createMenu/`, menuData);
    return response.data.data || response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const updateMenu = async (restaurantId, menuId, menuData) => {
  try {
    const response = await axios.put(`${API_URL}/users/restaurant/${restaurantId}/menus/${menuId}/update/`, menuData);
    return response.data.data || response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// The menu's public-website exposure alone, which never goes near the menu's contents.
export const updateMenuWebsite = async (restaurantId, menuId, payload) => {
  try {
    const response = await axios.put(`${API_URL}/users/restaurant/${restaurantId}/menus/${menuId}/website/`, payload);
    if (!response.data?.success) throw new Error(response.data?.error || 'Failed to save');
    return response.data.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const deleteMenu = async (restaurantId, menuId) => {
  try {
    const response = await axios.delete(`${API_URL}/users/restaurant/${restaurantId}/menus/${menuId}/delete/`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// ==================== MENU SECTIONS ====================
export const getMenuSections = async (restaurantId, menuId) => {
  try {
    const response = await axios.get(`${API_URL}/users/restaurant/${restaurantId}/menus/${menuId}/sections/`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const createMenuSection = async (restaurantId, menuId, sectionData) => {
  try {
    const response = await axios.post(`${API_URL}/users/restaurant/${restaurantId}/menus/${menuId}/sections/create/`, sectionData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const updateMenuSection = async (restaurantId, menuId, sectionId, sectionData) => {
  try {
    const response = await axios.put(`${API_URL}/users/restaurant/${restaurantId}/menus/${menuId}/sections/${sectionId}/update/`, sectionData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const deleteMenuSection = async (restaurantId, menuId, sectionId) => {
  try {
    const response = await axios.delete(`${API_URL}/users/restaurant/${restaurantId}/menus/${menuId}/sections/${sectionId}/delete/`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// ==================== MENU SECTION ITEMS ====================
export const getMenuSectionItems = async (restaurantId, menuId, sectionId) => {
  try {
    const response = await axios.get(`${API_URL}/users/restaurant/${restaurantId}/menus/${menuId}/sections/${sectionId}/items/`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const addMenuSectionItem = async (restaurantId, menuId, sectionId, itemData) => {
  try {
    const response = await axios.post(`${API_URL}/users/restaurant/${restaurantId}/menus/${menuId}/sections/${sectionId}/items/add/`, itemData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const updateMenuSectionItem = async (restaurantId, menuId, sectionId, itemId, itemData) => {
  try {
    const response = await axios.put(`${API_URL}/users/restaurant/${restaurantId}/menus/${menuId}/sections/${sectionId}/items/${itemId}/update/`, itemData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const deleteMenuSectionItem = async (restaurantId, menuId, sectionId, itemId) => {
  try {
    const response = await axios.delete(`${API_URL}/users/restaurant/${restaurantId}/menus/${menuId}/sections/${sectionId}/items/${itemId}/delete/`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};
